import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../services/api';
//import { toast } from 'react-hot-toast';
import toast from '../utils/toast';
import { DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PasteUploadArea from './PasteUploadArea';

const VerificationForm = ({ formSubmissionId, products, onVerificationComplete, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [verificationData, setVerificationData] = useState({});
  const [existingVerifications, setExistingVerifications] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [verifyingField, setVerifyingField] = useState(null);

  // Set initial selected product when products change
  useEffect(() => {
    if (products?.length > 0 && !selectedProduct) {
      setSelectedProduct(products[0]);
    }
  }, [products]);

  // Fetch verification types and existing verifications when component mounts or product changes
  useEffect(() => {
    let mounted = true;

    const fetchVerificationData = async () => {
      if (!formSubmissionId || !selectedProduct) return;
      
      try {
        setLoading(true);
        const response = await api.get(`/form-submissions/${formSubmissionId}/verifications`);
        
        if (!mounted) return;

        // Filter verifications for the selected product
        const productVerifications = response.data.filter(v => 
          v.productId === selectedProduct.id._id && 
          v.productIndex === selectedProduct.productIndex
        );

        setExistingVerifications(productVerifications);
        
        // Extract verification types from the response
        const types = productVerifications.map(v => v.verificationType).filter(Boolean);
        setVerificationTypes(types);

        // Initialize verification data
        const initialData = {};
        types.forEach(type => {
          const existing = productVerifications.find(v => 
            v.verificationType._id === type._id
          );
          initialData[type._id] = {
            value: existing?.value !== undefined ? existing.value : '',
            files: existing?.files || []
          };
        });
        setVerificationData(initialData);
      } catch (error) {
        console.error('Error fetching verifications:', error);
        if (mounted) {
          if (error.response?.status === 401) {
            toast.error('Session expired. Please log in again.');
          } else {
            toast.error('Failed to fetch verifications');
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchVerificationData();
    return () => {
      mounted = false;
    };
  }, [formSubmissionId, selectedProduct?.id._id, selectedProduct?.productIndex]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      
      // Submit all verifications
      const verificationPromises = Object.entries(verificationData).map(([typeId, data]) => {
        return api.post(`/form-submissions/${formSubmissionId}/verifications`, {
          productId: selectedProduct.id._id,
          productIndex: selectedProduct.productIndex,
          verificationTypeId: typeId,
          status: 'pending',
          files: data.files,
          value: data.value
        });
      });

      await Promise.all(verificationPromises);
      
      // Refresh verifications
      const response = await api.get(`/form-submissions/${formSubmissionId}/verifications`);
      const productVerifications = response.data.filter(v => 
        v.productId === selectedProduct.id._id && 
        v.productIndex === selectedProduct.productIndex
      );
      
      setExistingVerifications(productVerifications);
      toast.success('Verifications submitted successfully');
      
      if (onVerificationComplete) {
        onVerificationComplete();
      }
    } catch (error) {
      console.error('Error submitting verifications:', error);
      toast.error(error.response?.data?.message || 'Failed to submit verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (typeId, value) => {
    setVerificationData(prev => ({
      ...prev,
      [typeId]: {
        ...prev[typeId],
        value: value
      }
    }));
  };

  // Handle file upload for verification
  const handleFileUpload = async (verificationTypeId, file) => {
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('files', file);

      const response = await api.post(`/form-submissions/${formSubmissionId}/verification-files`, formData);

      setVerificationData(prev => ({
        ...prev,
        [verificationTypeId]: {
          ...prev[verificationTypeId],
          files: [...(prev[verificationTypeId]?.files || []), ...response.data]
        }
      }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  // Format the product display name with fees and index
  const getProductDisplayName = (product) => {
    const fees = [
      `Govt: ${product['Government Fees']}`,
      `Prof: ${product['Professional Fees']}`
    ];
    const otherFees = product['Other Fees'];
    if (otherFees && otherFees.size > 0) {
      Array.from(otherFees.entries()).forEach(([key, value]) => {
        fees.push(`${key}: ${value}`);
      });
    }
    return `${product.id.name} #${product.productIndex + 1} (${fees.join(', ')})`;
  };

  const handleVerifyField = async (verificationType) => {
    try {
      setVerifyingField(verificationType._id);
      const data = verificationData[verificationType._id];
      
      if (!data?.value && verificationType.type !== 'file') {
        toast.error('Please enter a value before verifying');
        return;
      }

      await api.post(`/form-submissions/${formSubmissionId}/verifications`, {
        productId: selectedProduct.id._id,
        productIndex: selectedProduct.productIndex,
        verificationTypeId: verificationType._id,
        status: 'approved',
        value: data?.value || '',
        files: data?.files || []
      });

      // Refresh verifications
      const response = await api.get(`/form-submissions/${formSubmissionId}/verifications`);
      const productVerifications = response.data.filter(v => 
        v.productId === selectedProduct.id._id && 
        v.productIndex === selectedProduct.productIndex
      );
      
      setExistingVerifications(productVerifications);
      toast.success(`${verificationType.label} verified successfully`);
      
      if (onVerificationComplete) {
        onVerificationComplete();
      }
    } catch (error) {
      console.error('Error verifying field:', error);
      toast.error(error.response?.data?.message || 'Failed to verify field');
    } finally {
      setVerifyingField(null);
    }
  };

  const renderVerificationField = (verificationType) => {
    const data = verificationData[verificationType._id] || { value: '', files: [] };
    const existing = existingVerifications.find(v => 
      v.verificationType._id === verificationType._id
    );

    return (
      <div key={verificationType._id} className="space-y-4 p-4 bg-white rounded-lg shadow">
        <div className="flex justify-between items-start">
          <label className="block text-sm font-medium text-gray-700">
            {verificationType.label}
            {verificationType.required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center gap-2">
            {existing && (
              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                existing.status === 'approved' ? 'bg-green-100 text-green-800' :
                existing.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {existing.status.charAt(0).toUpperCase() + existing.status.slice(1)}
              </span>
            )}
            <button
              type="button"
              onClick={() => handleVerifyField(verificationType)}
              disabled={loading || verifyingField === verificationType._id || existing?.status === 'approved'}
              className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm
                ${existing?.status === 'approved' 
                  ? 'bg-green-100 text-green-700 cursor-not-allowed'
                  : verifyingField === verificationType._id
                  ? 'bg-gray-100 text-gray-500 cursor-wait'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
            >
              {existing?.status === 'approved' ? (
                <CheckCircleIcon className="h-4 w-4 mr-1" />
              ) : null}
              {existing?.status === 'approved' 
                ? 'Verified' 
                : verifyingField === verificationType._id 
                ? 'Verifying...' 
                : 'Verify'}
            </button>
          </div>
        </div>
        
        {verificationType.type === 'text' && (
          <input
            type="text"
            value={data.value}
            onChange={(e) => handleInputChange(verificationType._id, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder={`Enter ${verificationType.label.toLowerCase()}`}
            required={verificationType.required}
            minLength={verificationType.validation?.minLength}
            maxLength={verificationType.validation?.maxLength}
            pattern={verificationType.validation?.pattern}
          />
        )}

        {verificationType.type === 'number' && (
          <input
            type="number"
            value={data.value}
            onChange={(e) => handleInputChange(verificationType._id, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder={`Enter ${verificationType.label.toLowerCase()}`}
            required={verificationType.required}
            min={verificationType.validation?.minValue}
            max={verificationType.validation?.maxValue}
            step={verificationType.validation?.step || 1}
          />
        )}

        {verificationType.type === 'file' && (
          <PasteUploadArea
            onFileSelect={(file) => handleFileUpload(verificationType._id, file)}
            accept={verificationType.validation?.allowedFileTypes?.join(',')}
            maxSize={(verificationType.validation?.maxFileSize || 10) * 1024 * 1024}
            className="mt-1"
            dragDropText="Upload a file"
            pasteText="or paste from clipboard (Ctrl+V)"
          >
            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <span>Upload a file</span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              {verificationType.validation?.allowedFileTypes?.join(', ') || 'PDF, DOC, DOCX'} up to {verificationType.validation?.maxFileSize || 10}MB
            </p>
          </PasteUploadArea>
        )}

        {verificationType.type === 'otp-email' && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={data.value}
                onChange={(e) => handleInputChange(verificationType._id, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter verification code"
                required={verificationType.required}
                pattern={`[0-9]{${verificationType.validation?.otpLength || 6}}`}
                maxLength={verificationType.validation?.otpLength || 6}
              />
              <button
                type="button"
                onClick={() => handleResendOTP(verificationType._id)}
                className="mt-1 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
            {verificationType.validation?.otpExpiry && (
              <p className="text-xs text-gray-500">
                Code will expire in {verificationType.validation.otpExpiry} minutes
              </p>
            )}
          </div>
        )}

        {verificationType.description && (
          <p className="mt-2 text-sm text-gray-500">{verificationType.description}</p>
        )}

        {/* Show uploaded files if any */}
        {data.files && data.files.length > 0 && (
          <div className="mt-2">
            <h4 className="text-sm font-medium text-gray-700">Uploaded files:</h4>
            <ul className="mt-1 divide-y divide-gray-200">
              {data.files.map((file, index) => (
                <li key={index} className="py-2 flex items-center">
                  <DocumentArrowUpIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{file.originalName || file.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Add the OTP resend handler
  const handleResendOTP = async (verificationTypeId) => {
    try {
      setLoading(true);
      await api.post(`/form-submissions/${formSubmissionId}/resend-otp`, {
        verificationTypeId,
        productId: selectedProduct.id._id,
        productIndex: selectedProduct.productIndex
      });
      toast.success('Verification code sent successfully');
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(error.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  if (!products?.length) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Modal header - fixed */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Checklist
              </h3>
              <button
                type="button"
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Product selector */}
            {products.length > 1 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Select Product
                </label>
                <select
                  value={selectedProduct ? JSON.stringify({ id: selectedProduct.id._id, index: selectedProduct.productIndex }) : ''}
                  onChange={(e) => {
                    const { id, index } = JSON.parse(e.target.value);
                    const product = products.find(p => p.id._id === id && p.productIndex === index);
                    setSelectedProduct(product);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {products.map((product) => (
                    <option 
                      key={`${product.id._id}-${product.productIndex}`}
                      value={JSON.stringify({ id: product.id._id, index: product.productIndex })}
                    >
                      {getProductDisplayName(product)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Modal body - scrollable */}
          <div className="flex-1 px-4 pt-5 pb-4 sm:p-6 overflow-y-auto max-h-[60vh]">
            <div className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading verification fields...</span>
                </div>
              ) : verificationTypes.length > 0 ? (
                <div className="space-y-4">
                  {verificationTypes.map(renderVerificationField)}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No verification fields available for this product.</p>
              )}
            </div>
          </div>

          {/* Modal footer - fixed */}
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-2 border-t">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Verification'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationForm; 